import { useState } from "react";
import { Hash, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * WHA-3468 / WHA-3469 — chip family for PM issues attached to the chatbot
 * context. Mirrors the `DocChip` family: single chips up to 2, then a
 * collapsed "Issue N" chip with a popover listing every attached issue.
 * Clicking a chip navigates to the issue page (handled by the caller).
 */
export interface IssueChipData {
  /** PM issue id — the navigation target (`?selected={id}`). */
  id: number;
  /** Human-facing issue number (`#42`). */
  issueNumber: number;
  title: string;
}

/** `tray` = chatbot input area, `message` = sent user message bubble. */
type IssueChipVariant = "tray" | "message";

const SINGLE_CHIP_CLASSES: Record<IssueChipVariant, string> = {
  tray: "inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-lg border border-neutral-200 bg-neutral-100 px-2.5 py-1 text-sm text-neutral-700 transition-colors hover:bg-neutral-200",
  message:
    "inline-flex h-6 min-w-0 max-w-full items-center gap-1.5 rounded bg-neutral-100 px-2 text-xs text-neutral-700 transition-colors hover:bg-neutral-200",
};

const COLLAPSED_CHIP_CLASSES: Record<IssueChipVariant, string> = {
  tray: "inline-flex h-7 max-w-full items-center gap-1.5 rounded-lg border border-neutral-200 bg-neutral-100 px-2.5 text-sm text-neutral-700 transition-colors hover:bg-neutral-200",
  message:
    "inline-flex h-6 max-w-full items-center gap-1.5 rounded bg-neutral-100 px-2 text-xs text-neutral-700 transition-colors hover:bg-neutral-200",
};

const ICON_SIZE: Record<IssueChipVariant, string> = {
  tray: "h-3.5 w-3.5",
  message: "h-3.5 w-3.5",
};

interface IssueChipProps {
  issue: IssueChipData;
  /** Click handler — navigates to the issue page. */
  onClick: () => void;
  /** When provided, renders a remove (X) button (input-tray variant). */
  onRemove?: () => void;
  variant?: IssueChipVariant;
  className?: string;
}

/**
 * Single issue chip — `Hash` icon + `#N title`. Clicking navigates to the
 * issue; the optional remove button detaches it from the chat context.
 */
export function IssueChip({
  issue,
  onClick,
  onRemove,
  variant = "message",
  className,
}: IssueChipProps) {
  const label = `#${issue.issueNumber} ${issue.title}`;

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn(SINGLE_CHIP_CLASSES[variant], className)}>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClick();
              }}
              className="flex min-w-0 items-center gap-1.5 focus:outline-none"
            >
              <Hash className={cn(ICON_SIZE[variant], "shrink-0 text-neutral-500")} />
              <span className="min-w-0 truncate">
                {issue.issueNumber} {issue.title}
              </span>
            </button>
            {onRemove && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRemove();
                }}
                className="ml-0.5 shrink-0 rounded-sm text-neutral-400 hover:bg-neutral-200 hover:text-neutral-600"
                aria-label={`Remove issue #${issue.issueNumber}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-sm font-medium">{label}</p>
          <p className="text-xs text-neutral-400">Click to open the issue</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface IssueChipCollapsedProps {
  issues: IssueChipData[];
  /** Click handler for each issue (receives the issue). */
  onIssueClick: (issue: IssueChipData) => void;
  /** When provided, popover rows render a remove (X) button. */
  onRemove?: (issueId: number) => void;
  variant?: IssueChipVariant;
  className?: string;
}

/**
 * Collapsed issue chip shown for 3+ attached issues. The trigger reads
 * "Issue" + a count badge; clicking opens a popover listing every issue.
 * Mirrors `DocChipCollapsed`.
 */
export function IssueChipCollapsed({
  issues,
  onIssueClick,
  onRemove,
  variant = "message",
  className,
}: IssueChipCollapsedProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={cn(COLLAPSED_CHIP_CLASSES[variant], className)}>
          <Hash className={cn(ICON_SIZE[variant], "shrink-0 text-neutral-500")} />
          <span>Issue</span>
          <span className="ml-0.5 rounded bg-neutral-200 px-1 text-[10px] font-medium text-neutral-600">
            {issues.length}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={variant === "tray" ? "start" : "end"}
        side="top"
        className="w-max min-w-56 max-w-[min(28rem,90vw)] p-1"
      >
        <div className="flex flex-col">
          {issues.map((issue) => (
            <div
              key={issue.id}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-neutral-700 hover:bg-neutral-100"
            >
              <button
                type="button"
                className="flex min-w-0 flex-1 items-start gap-2 text-left focus:outline-none"
                onClick={() => {
                  onIssueClick(issue);
                  setOpen(false);
                }}
              >
                <Hash className="mt-0.5 h-3.5 w-3.5 shrink-0 text-neutral-500" />
                <span className="min-w-0 whitespace-normal break-words">
                  #{issue.issueNumber} {issue.title}
                </span>
              </button>
              {onRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(issue.id)}
                  className="shrink-0 rounded-sm p-0.5 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-600"
                  aria-label={`Remove issue #${issue.issueNumber}`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface IssueChipGroupProps {
  issues: IssueChipData[];
  /** Click handler for each issue — navigates to the issue page. */
  onIssueClick: (issue: IssueChipData) => void;
  /** When provided, chips render a remove (X) button (input-tray variant). */
  onRemove?: (issueId: number) => void;
  variant?: IssueChipVariant;
  className?: string;
}

/**
 * Renders attached PM issues: individual chips for 1-2 issues, a single
 * collapsed "Issue N" chip with a popover for 3+ (WHA-3468).
 */
export function IssueChipGroup({
  issues,
  onIssueClick,
  onRemove,
  variant = "message",
  className,
}: IssueChipGroupProps) {
  if (issues.length === 0) return null;

  // Collapsed view for 3+ issues — "more than 2 are added".
  if (issues.length >= 3) {
    return (
      <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
        <IssueChipCollapsed
          issues={issues}
          onIssueClick={onIssueClick}
          onRemove={onRemove}
          variant={variant}
        />
      </div>
    );
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      {issues.map((issue) => (
        <IssueChip
          key={issue.id}
          issue={issue}
          onClick={() => onIssueClick(issue)}
          onRemove={onRemove ? () => onRemove(issue.id) : undefined}
          variant={variant}
        />
      ))}
    </div>
  );
}
